import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  return useContext(SubscriptionContext);
}

// Name kept as FREE_MONTHLY_LIMIT for backward compat with existing imports,
// but this never resets monthly — it's a lifetime trial cap, matching the
// real server-side enforcement in server/utils/credits.js (CREDIT_LIMIT).
export const FREE_MONTHLY_LIMIT = 50;

export const CREDIT_COSTS = {
  text:    1,
  design:  4,
  video:   10,
  audio:   3,
  agents:  1,
  toolkit: 2,
};

export const PLAN_CATEGORIES = {
  free:      ['marketing', 'content'],
  grow:      ['marketing', 'content', 'digital'],
  scale:     ['marketing', 'content', 'digital', 'strategy', 'ecommerce', 'agency', 'creative', 'voice-ai'],
  evolution: null,  // all categories
  admin:     null,  // all categories
};

export const PLANS = {
  free: {
    name: 'Free',
    monthlyLimit: FREE_MONTHLY_LIMIT,
    allCategories: true,
  },
  grow: {
    name: 'Grow',
    allCategories: true,
    unlimitedUsage: true,
    teamAccess: true,
  },
  scale: {
    name: 'Scale',
    allCategories: true,
    unlimitedUsage: true,
    teamAccess: true,
  },
  evolution: {
    name: 'Evolution',
    allCategories: true,
    unlimitedUsage: true,
    teamAccess: true,
    apiAccess: true,
    whiteLabel: true,
  },
  admin: {
    name: 'Admin',
    allAccess: true,
    unlimitedUsage: true,
    teamAccess: true,
    apiAccess: true,
    whiteLabel: true,
  },
};

export function SubscriptionProvider({ children }) {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const [subscription, setSubscription] = useState('free');
  const [usageCount, setUsageCount] = useState(0);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  const ADMIN_UIDS = (process.env.REACT_APP_ADMIN_UIDS || '').split(',').map(s => s.trim()).filter(Boolean);
  const ADMIN_EMAILS = ['gabriela.ormazabal@gormaran-marketing.com', 'gabriela.ormazabal@gmail.com'];

  useEffect(() => {
    const email = currentUser?.email?.toLowerCase().trim();
    if (ADMIN_UIDS.includes(currentUser?.uid) || ADMIN_EMAILS.includes(email)) {
      setSubscription('admin');
      setUsageCount(0);
      setCheckingSubscription(false);
      return;
    }
    if (userProfile) {
      const raw = userProfile.subscription || 'free';
      setSubscription(raw);

      // Free plan = 50-credit lifetime trial. Never reset.
      // Paid plans are unlimitedUsage — no reset needed either.
      setUsageCount(userProfile.usageCount || 0);
      setCheckingSubscription(false);
    } else {
      setCheckingSubscription(false);
    }
  }, [userProfile, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Access functions ---

  function getPlan() {
    return PLANS[subscription] || PLANS.free;
  }

  function isInTrial() { return false; }
  function trialDaysRemaining() { return 0; }

  function canUseSpecificTool(categoryId) {
    if (!currentUser) return false;
    return true; // All plans have access to all tools; usage quota enforced separately
  }

  function canUseTool(categoryId) {
    if (!currentUser) return false;
    return true;
  }

  function isCategoryLocked(categoryId) {
    const allowed = PLAN_CATEGORIES[subscription];
    if (allowed === null || allowed === undefined) return false; // evolution/admin
    return !allowed.includes(categoryId);
  }

  function hasMonthlyUsageLeft() {
    const plan = getPlan();
    if (plan.allAccess || plan.unlimitedUsage) return true;
    return usageCount < FREE_MONTHLY_LIMIT;
  }

  async function trackUsage(cost = 1) {
    if (!currentUser) return;
    const plan = getPlan();
    if (plan.allAccess || plan.unlimitedUsage) return; // admin / paid — skip tracking
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { usageCount: increment(cost) });
      setUsageCount((prev) => prev + cost);
    } catch (err) {
      console.error('Error tracking usage:', err);
    }
  }

  function getPlanLimits() {
    return PLANS[subscription] || PLANS.free;
  }

  const value = {
    subscription,
    usageCount,
    checkingSubscription,
    canUseTool,
    canUseSpecificTool,
    isCategoryLocked,
    isInTrial,
    trialDaysRemaining,
    hasMonthlyUsageLeft,
    trackUsage,
    getPlanLimits,
    PLANS,
    FREE_MONTHLY_LIMIT,
    CREDIT_COSTS,
    PLAN_CATEGORIES,
    refreshUserProfile,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
